/**
 * Spend-over-time, stacked by source project. Follows SignupsChart's recharts + Parslee
 * token conventions; the series palette is the dataviz-validated categorical set in
 * tokenUsageView.
 *
 * The per-series totals list beside the chart is not decoration: it is the table view
 * that keeps every project's exact number readable regardless of stack order, and it
 * carries the legend swatches so series identity is never colour-alone.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatTokens } from '../OrganizationsTab';
import {
  formatExactTokens,
  type SourceProjectSeries,
} from './tokenUsageView';

const AXIS_COLOR = '#575859'; // text secondary

export interface TokenUsageChartProps {
  series: SourceProjectSeries;
}

export function TokenUsageChart({ series }: TokenUsageChartProps) {
  const empty = series.totalTokens === 0;
  // Named rather than left anonymous — a folded project is still a real project.
  const foldedIntoOther = series.foldedIntoOther;

  const srDescription = empty
    ? 'Token spend by source project: no recorded spend in this window.'
    : `Token spend by source project, daily UTC buckets, ${series.rows.length} days, ` +
      `total ${formatExactTokens(series.totalTokens)} tokens across ` +
      `${series.keys.length} source project(s).`;

  return (
    <div className="mb-6 border border-border rounded-md p-4 bg-card">
      <div className="mb-3">
        <h3 className="text-sm font-medium text-foreground">
          Recorded spend over time
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Stacked by source project, bucketed on the server ingest day (UTC)
        </p>
      </div>

      <span className="sr-only" role="img" aria-label={srDescription}>
        {srDescription}
      </span>

      {empty ? (
        <div className="py-12 text-center text-muted-foreground">
          No recorded spend in this window.
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={series.rows}>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-border"
              />
              <XAxis dataKey="dateUtc" stroke={AXIS_COLOR} tick={{ fontSize: 11 }} />
              <YAxis
                allowDecimals={false}
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => formatTokens(v)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #d4d4d8',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
                formatter={(value, name) => [formatExactTokens(value as number), name as string]}
                cursor={{ fill: '#8A8F98', fillOpacity: 0.12 }}
              />
              {series.keys.map(key => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="spend"
                  fill={series.colors[key]}
                  // 2px surface gap between stacked segments (dataviz mark spec).
                  stroke="#ffffff"
                  strokeWidth={1}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>

          <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {series.keys.map(key => (
              <li key={key} className="flex items-center gap-2 text-xs">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: series.colors[key] }}
                />
                <span className="text-text-secondary">{key}</span>
                <span className="font-medium text-foreground">
                  {formatExactTokens(series.totals[key] ?? 0)}
                </span>
              </li>
            ))}
          </ul>

          {foldedIntoOther.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Other combines: {foldedIntoOther.join(', ')}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
